import app from 'flarum/forum/app';
import IndexPage from 'flarum/forum/components/IndexPage';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import Discussion from 'flarum/common/models/Discussion';
import Post from 'flarum/common/models/Post';
import LogInModal from 'flarum/forum/components/LogInModal';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import Model from 'flarum/common/Model';
import Stream from 'flarum/common/utils/Stream';
import Page from 'flarum/common/components/Page';
import LinkButton from 'flarum/common/components/LinkButton';
import Link from 'flarum/common/components/Link';
import TextEditor from 'flarum/common/components/TextEditor';
import TextEditorButton from 'flarum/common/components/TextEditorButton';
import ComposerBody from 'flarum/forum/components/ComposerBody';
import ReplyComposer from 'flarum/forum/components/ReplyComposer';
import EditPostComposer from 'flarum/forum/components/EditPostComposer';
import CommentPost from 'flarum/forum/components/CommentPost';
import { extend, override } from 'flarum/common/extend';
import CoursesPage from './components/CoursesPage';
import AllDiscussionsPage from './components/AllDiscussionsPage';
import QuantumSurfLandingPage from './components/QuantumSurfLandingPage';
import UnclePataLandingPage from './components/UnclePataLandingPage';
import WwaLandingPage from './components/WwaLandingPage';
import WwaFaqPage from './components/WwaFaqPage';

function isLoggedIn() {
  return !!(app.session && app.session.user);
}

// Post-login return-URL handling. Hosts under deep-integrations.com (notably
// the wiki subdomain) redirect anonymous visitors here with a ?return=<url>
// query param. If the user lands with one and is logged in, send them back.
// The allowlist prevents the param from being abused as an open redirect.
function getValidatedReturnUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const target = params.get('return');
    if (!target) return null;
    const url = new URL(target);
    if (url.protocol !== 'https:') return null;
    if (url.hostname !== 'deep-integrations.com' &&
        !url.hostname.endsWith('.deep-integrations.com')) {
      return null;
    }
    return url.toString();
  } catch (_) {
    return null;
  }
}

function maybeRedirectToReturnUrl() {
  if (!isLoggedIn()) return;
  const target = getValidatedReturnUrl();
  if (!target) return;
  window.location.replace(target);
}

function syncBodyClass() {
  const body = document.body;
  if (!body) return;

  body.classList.remove('user-logged-in', 'user-guest');
  body.classList.add(isLoggedIn() ? 'user-logged-in' : 'user-guest');
}

function getAudioExtension(mimeType) {
  if (!mimeType) return 'webm';
  if (mimeType.includes('mp4')) return 'mp4';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('mpeg') || mimeType.includes('mp3')) return 'mp3';
  if (mimeType.includes('wav')) return 'wav';
  return 'webm';
}

function normalizeAudioMimeType(mimeType) {
  return (mimeType || '').split(';')[0].trim().toLowerCase();
}

function isSupportedRecordedMimeType(mimeType) {
  return mimeType.startsWith('audio/') || mimeType === 'video/webm';
}

function replaceLastOccurrence(content, search, replacement) {
  if (!search) return content;

  const index = content.lastIndexOf(search);

  if (index === -1) return content;

  return `${content.slice(0, index)}${replacement}${content.slice(index + search.length)}`;
}

const MIC_STATUS = {
  idle: 'idle',
  recording: 'recording',
  uploading: 'uploading',
  failed: 'failed',
};

function createMicState() {
  return {
    status: MIC_STATUS.idle,
    recorder: null,
    stream: null,
    chunks: [],
    mimeType: '',
    error: null,
  };
}

function stopMicStream(state) {
  if (!state?.stream) return;

  state.stream.getTracks().forEach((track) => track.stop());
  state.stream = null;
}

function getSupportedMicMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';

  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ];

  return candidates.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || '';
}

function getMicButtonProps(state) {
  if (state.status === MIC_STATUS.recording) {
    return {
      icon: 'fas fa-stop',
      title: 'Stop recording',
    };
  }

  if (state.status === MIC_STATUS.uploading) {
    return {
      icon: 'fas fa-spinner fa-spin',
      title: 'Uploading audio',
    };
  }

  if (state.status === MIC_STATUS.failed) {
    return {
      icon: 'fas fa-microphone-slash',
      title: state.error || 'Recording failed',
    };
  }

  return {
    icon: 'fas fa-microphone',
    title: 'Record audio',
  };
}

async function uploadRecordedBlob(blob, mimeType) {
  const incomingMimeType = mimeType || blob.type || 'audio/webm';
  const normalizedMimeType = normalizeAudioMimeType(incomingMimeType) || 'audio/webm';
  const extension = getAudioExtension(normalizedMimeType);
  const fileName = `recording-${Date.now()}.${extension}`;
  const file = new File([blob], fileName, {
    type: normalizedMimeType,
    lastModified: Date.now(),
  });
  const body = new FormData();

  body.append('files[]', file);

  console.log('[audio-recorder] upload request', {
    blobType: blob.type,
    incomingMimeType,
    normalizedMimeType,
    name: file.name,
    type: file.type,
    size: file.size,
  });

  const response = await app.request({
    method: 'POST',
    url: app.forum.attribute('apiUrl') + '/fof/upload',
    serialize: (raw) => raw,
    body,
  });

  return response?.data?.[0]?.attributes || response?.data?.[0] || null;
}

function insertRecordedAudioMarkup(editor, uploadedFile) {
  const url = uploadedFile?.url;
  const mimeType = normalizeAudioMimeType(uploadedFile?.type);

  if (!url || !mimeType || !isSupportedRecordedMimeType(mimeType)) {
    throw new Error('Upload succeeded but no valid audio URL was returned');
  }

  const extension = getAudioExtension(mimeType);
  const markup = `[audio ${extension}="${url}"]\n`;

  console.log('[audio-recorder] inserted composer markup', {
    url,
    mimeType,
    markup,
  });

  editor.attrs.composer.editor.insertAtCursor(markup, false);
}

function wrapUploadedAudioBbcode(editor, file) {
  const mimeType = typeof file?.type === 'function' ? file.type() : null;
  const url = typeof file?.url === 'function' ? file.url() : null;
  const inserted = typeof file?.bbcode === 'function' ? file.bbcode() : null;

  if (!mimeType || !mimeType.startsWith('audio/') || !url || !inserted) return;

  const extension = getAudioExtension(mimeType);
  const replacement = `[audio ${extension}="${url}"]`;
  const currentContent = editor.attrs?.composer?.fields?.content?.() || '';
  const nextContent = replaceLastOccurrence(currentContent, inserted, replacement);

  console.log('[audio-recorder] wrapping regular upload', {
    mimeType,
    url,
    replacement,
  });

  if (nextContent !== currentContent) {
    editor.attrs.composer.fields.content(nextContent);
    m.redraw();
  }
}

async function startMicRecording(editor) {
  const state = editor.deepIntegrationsMicState;

  if (!navigator.mediaDevices?.getUserMedia) {
    state.status = MIC_STATUS.failed;
    state.error = 'Microphone recording is not supported in this browser';
    app.alerts.show({ type: 'error' }, state.error);
    console.error('[audio-recorder] microphone unsupported');
    m.redraw();
    return;
  }

  if (typeof MediaRecorder === 'undefined') {
    state.status = MIC_STATUS.failed;
    state.error = 'MediaRecorder is not available in this browser';
    app.alerts.show({ type: 'error' }, state.error);
    console.error('[audio-recorder] MediaRecorder unavailable');
    m.redraw();
    return;
  }

  try {
    console.log('[audio-recorder] requesting microphone permission');
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const supportedMimeType = getSupportedMicMimeType();
    const recorder = supportedMimeType ? new MediaRecorder(stream, { mimeType: supportedMimeType }) : new MediaRecorder(stream);

    state.stream = stream;
    state.recorder = recorder;
    state.chunks = [];
    state.mimeType = recorder.mimeType || supportedMimeType || '';
    state.error = null;

    recorder.addEventListener('dataavailable', (event) => {
      if (event.data && event.data.size > 0) {
        state.chunks.push(event.data);
      }
    });

    recorder.addEventListener('stop', async () => {
      const mimeType = state.mimeType || recorder.mimeType || state.chunks[0]?.type || '';
      const normalizedMimeType = normalizeAudioMimeType(mimeType) || 'audio/webm';
      const blob = new Blob(state.chunks, { type: normalizedMimeType });

      console.log('[audio-recorder] recorder stop', {
        mimeType,
        normalizedMimeType,
        size: blob.size,
        chunks: state.chunks.length,
      });

      stopMicStream(state);
      state.recorder = null;
      state.chunks = [];
      state.status = MIC_STATUS.uploading;
      m.redraw();

      try {
        const uploadedFile = await uploadRecordedBlob(blob, normalizedMimeType);
        console.log('[audio-recorder] upload response', uploadedFile);
        insertRecordedAudioMarkup(editor, uploadedFile);
        state.status = MIC_STATUS.idle;
        state.error = null;
      } catch (error) {
        state.status = MIC_STATUS.failed;
        state.error = error?.response?.errors?.[0]?.detail || error?.message || 'Audio upload failed';
        app.alerts.show({ type: 'error' }, state.error);
        console.error('[audio-recorder] upload failed', error);

        if (error?.responseText) {
          console.error('[audio-recorder] upload raw response', error.responseText);
        }

        if (error?.xhr?.responseText) {
          console.error('[audio-recorder] upload xhr response', error.xhr.responseText);
        }
      }

      m.redraw();
    });

    recorder.addEventListener('error', (event) => {
      state.status = MIC_STATUS.failed;
      state.error = event?.error?.message || 'Recording failed';
      stopMicStream(state);
      state.recorder = null;
      state.chunks = [];
      app.alerts.show({ type: 'error' }, state.error);
      console.error('[audio-recorder] recorder error', event);
      m.redraw();
    });

    recorder.start();
    state.status = MIC_STATUS.recording;
    console.log('[audio-recorder] recorder start', {
      mimeType: state.mimeType,
    });
    m.redraw();
  } catch (error) {
    state.status = MIC_STATUS.failed;
    state.error = error?.message || 'Microphone access failed';
    stopMicStream(state);
    state.recorder = null;
    state.chunks = [];
    app.alerts.show({ type: 'error' }, state.error);
    console.error('[audio-recorder] microphone permission failed', error);
    m.redraw();
  }
}

function stopMicRecording(editor) {
  const state = editor.deepIntegrationsMicState;

  if (!state?.recorder || state.recorder.state !== 'recording') return;

  console.log('[audio-recorder] stopping recorder');
  state.recorder.stop();
}

function openLogInModal() {
  app.modal.show(LogInModal);
}

function openSignUpModal() {
  app.modal.show(SignUpModal, { courseEnrollmentKey: 'course-wwa-101' });
}

const COURSE_INTEGRATION_MAX = 500;
const COURSE_INTEGRATION_MIN_BODY_CHARS = 500;
const COURSE_INTEGRATION_PARENT_TAG_SLUG = 'courses';
const COURSE_INTEGRATION_CHILD_TAG_PREFIX = 'course-';
const POSTMENTION_ID_REGEX = /\[POSTMENTION\b[^\]]*?\b(?:id|post)="(\d+)"/gi;

function getCourseIntegrationDiscussion(body) {
  if (body instanceof ReplyComposer) return body.attrs?.discussion;
  if (body instanceof EditPostComposer) return body.attrs?.post?.discussion?.();
  return null;
}

function isCourseModuleDiscussion(discussion) {
  if (!discussion || typeof discussion.tags !== 'function') return false;
  const tags = discussion.tags();
  if (!Array.isArray(tags)) return false;

  const slugs = tags.map((tag) => (typeof tag?.slug === 'function' ? tag.slug() : null)).filter(Boolean);
  const hasParent = slugs.includes(COURSE_INTEGRATION_PARENT_TAG_SLUG);
  const hasCourseChild = slugs.some((slug) => slug.startsWith(COURSE_INTEGRATION_CHILD_TAG_PREFIX));

  return hasParent && hasCourseChild;
}

function formatCourseUnlockDate(iso) {
  if (!iso) return null;
  try {
    const dateText = new Date(iso).toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    return `${dateText} at 8:00 AM Eastern Time`;
  } catch (_) {
    return null;
  }
}

function renderCourseModuleLockedSplash(discussion) {
  const courseTag = discussion.tags?.()?.find((tag) => tag.slug?.()?.startsWith('course-'));
  const availableAt = typeof discussion.courseModuleAvailableAt === 'function'
    ? discussion.courseModuleAvailableAt()
    : null;
  const whenText = formatCourseUnlockDate(availableAt);

  return (
    <div className="CourseModuleLockedSplash">
      <div className="CourseModuleLockedSplash-icon">
        <i className="fas fa-lock" aria-hidden="true" />
      </div>
      <h1 className="CourseModuleLockedSplash-title">{discussion.title()}</h1>
      <p className="CourseModuleLockedSplash-subtitle">This module is locked.</p>
      {whenText
        ? <p className="CourseModuleLockedSplash-when">Available on <strong>{whenText}</strong>.</p>
        : <p className="CourseModuleLockedSplash-when">Complete the previous module to unlock this one.</p>
      }
      {courseTag ? (
        <Link
          className="Button Button--primary CourseModuleLockedSplash-back"
          href={app.route('blogCategory', { slug: courseTag.slug() })}
        >
          ← Back to {typeof courseTag.name === 'function' ? courseTag.name() : 'course'}
        </Link>
      ) : (
        <Link className="Button Button--primary CourseModuleLockedSplash-back" href={app.route('courses')}>
          ← Back to your courses
        </Link>
      )}
    </div>
  );
}

function isFirstLevelContent(content, discussion) {
  if (!content) return true;

  const firstPostId = discussion && typeof discussion.firstPost === 'function' ? discussion.firstPost()?.id?.() : null;
  const firstPostIdNum = firstPostId ? parseInt(firstPostId, 10) : null;

  const matches = [...content.matchAll(POSTMENTION_ID_REGEX)];
  if (matches.length === 0) return true;

  return matches.every((m) => {
    const mentionedId = parseInt(m[1], 10);
    return firstPostIdNum !== null && mentionedId === firstPostIdNum;
  });
}

function computeShouldShowActionsField(body) {
  if (!(body instanceof ReplyComposer) && !(body instanceof EditPostComposer)) return false;

  const discussion = getCourseIntegrationDiscussion(body);
  if (!isCourseModuleDiscussion(discussion)) return false;

  // On edit, always surface the field so the user can view/change/remove
  // the saved actions. The first-level/nested gate only applies to new
  // replies where we want to keep nested sub-threads single-field.
  if (body instanceof EditPostComposer) return true;

  const content = body.composer?.fields?.content?.() ?? '';
  return isFirstLevelContent(content, discussion);
}

function shouldShowActionsField(body) {
  // Decision is cached at oninit (see the ComposerBody oninit extend
  // below) to prevent typing-driven redraws from flipping it. ReplyComposer
  // also re-checks on the next tick so POSTMENTION insertion by
  // flarum/mentions can flip it to hidden for nested replies.
  if (body && typeof body._courseIntegrationShow === 'boolean') {
    return body._courseIntegrationShow;
  }
  return computeShouldShowActionsField(body);
}

function renderActionsField(body) {
  const actionsStream = body.composer.fields.actions;
  const value = typeof actionsStream === 'function' ? actionsStream() : '';
  const length = value ? value.length : 0;
  const counterClass =
    length > COURSE_INTEGRATION_MAX ? 'CourseIntegrationActions-counter CourseIntegrationActions-counter--over'
    : length > 400 ? 'CourseIntegrationActions-counter CourseIntegrationActions-counter--warn'
    : 'CourseIntegrationActions-counter';

  return (
    <div className="CourseIntegrationActions">
      <label className="CourseIntegrationActions-label">
        {app.translator.trans('deepintegrations-custom-blog-ecourse.forum.course_integration.actions_label')}
        <span className="CourseIntegrationActions-optional"> {app.translator.trans('deepintegrations-custom-blog-ecourse.forum.course_integration.actions_optional_hint', { max: COURSE_INTEGRATION_MAX })}</span>
      </label>
      <textarea
        className="FormControl CourseIntegrationActions-input"
        rows="3"
        maxlength={COURSE_INTEGRATION_MAX + 50}
        value={value}
        placeholder={app.translator.trans('deepintegrations-custom-blog-ecourse.forum.course_integration.actions_placeholder').toString()}
        onkeydown={(e) => e.stopPropagation()}
        oninput={(e) => {
          if (typeof actionsStream === 'function') {
            actionsStream(e.target.value);
          }
        }}
      />
      <div className={counterClass}>
        {length} / {COURSE_INTEGRATION_MAX}
      </div>
    </div>
  );
}

function findVdomNodeByExactClass(node, className) {
  if (!node || typeof node !== 'object') return null;
  if (node.attrs?.className === className) return node;
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      const found = findVdomNodeByExactClass(child, className);
      if (found) return found;
    }
  }
  return null;
}

function findVdomParentWithChildOfClass(node, className) {
  if (!node || typeof node !== 'object') return null;
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      if (child && typeof child === 'object' && child.attrs?.className === className) {
        return { parent: node, index: node.children.indexOf(child) };
      }
      const deeper = findVdomParentWithChildOfClass(child, className);
      if (deeper) return deeper;
    }
  }
  return null;
}

function injectActionsFieldIntoComposerVdom(vdom, body) {
  if (!vdom || typeof vdom !== 'object') return;

  const hit = findVdomParentWithChildOfClass(vdom, 'ComposerBody-editor');
  if (!hit || !Array.isArray(hit.parent.children)) return;

  const alreadyInjected = hit.parent.children.some((child) => child?.attrs?.className === 'CourseIntegrationActions');
  if (alreadyInjected) return;

  // Insert immediately before the editor so the layout reads: header, actions, editor.
  hit.parent.children.splice(hit.index, 0, renderActionsField(body));
}

function renderMainIntegrationCounter(body) {
  const contentStream = body.composer?.fields?.content;
  const content = typeof contentStream === 'function' ? (contentStream() || '') : '';
  const length = content.length;
  const isUnder = length < COURSE_INTEGRATION_MIN_BODY_CHARS;
  const counterClass = isUnder
    ? 'CourseIntegrationMain-counter CourseIntegrationMain-counter--under'
    : 'CourseIntegrationMain-counter CourseIntegrationMain-counter--hit';

  return (
    <div className="CourseIntegrationMain">
      <div className={counterClass}>
        <span className="CourseIntegrationMain-count">{length} / {COURSE_INTEGRATION_MIN_BODY_CHARS} characters</span>
        {isUnder && (
          <span className="CourseIntegrationMain-message">
            {' '}— please integrate more to complete this module, minimum not hit.
          </span>
        )}
      </div>
    </div>
  );
}

function injectMainIntegrationCounterIntoComposerVdom(vdom, body) {
  if (!vdom || typeof vdom !== 'object') return;

  const hit = findVdomParentWithChildOfClass(vdom, 'ComposerBody-editor');
  if (!hit || !Array.isArray(hit.parent.children)) return;

  const alreadyInjected = hit.parent.children.some((child) => child?.attrs?.className === 'CourseIntegrationMain');
  if (alreadyInjected) return;

  // Insert immediately after the editor so the layout reads: header, actions, editor, counter.
  hit.parent.children.splice(hit.index + 1, 0, renderMainIntegrationCounter(body));
}

const SUCCESS_ALERT_AUTODISMISS_MS = 5200;

function installSuccessAlertAutoDismiss() {
  if (!app.alerts || typeof app.alerts.show !== 'function') return;
  if (app.alerts.__deepIntegrationsAutoDismissPatched) return;

  const originalShow = app.alerts.show.bind(app.alerts);

  app.alerts.show = function (...args) {
    const id = originalShow(...args);

    // show(children), show(attrs, children), or show(componentClass, attrs, children).
    let attrs = null;
    if (args.length === 2) attrs = args[0];
    else if (args.length === 3) attrs = args[1];

    if (attrs && attrs.type === 'success') {
      setTimeout(() => {
        try {
          app.alerts.dismiss(id);
        } catch (e) {
          // Alert may already be gone; ignore.
        }
      }, SUCCESS_ALERT_AUTODISMISS_MS);
    }

    return id;
  };

  app.alerts.__deepIntegrationsAutoDismissPatched = true;
}

app.initializers.add('deep-integrations-theme', () => {
  installSuccessAlertAutoDismiss();

  // Load the stylized display font used for WWA headers. @import inside Flarum's
  // concatenated CSS gets ignored (not at top of sheet), so inject a <link>.
  if (typeof document !== 'undefined' && !document.getElementById('wwa-display-font')) {
    const link = document.createElement('link');
    link.id = 'wwa-display-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&display=swap';
    document.head.appendChild(link);
  }

  // Global site footer. Mounted on <body> outside the mithril #app mount so it
  // persists across SPA navigation and shows on every page.
  if (typeof document !== 'undefined' && !document.getElementById('wwa-site-footer')) {
    const footer = document.createElement('footer');
    footer.id = 'wwa-site-footer';
    footer.className = 'WwaFooter';
    const year = new Date().getFullYear();
    footer.innerHTML =
      '<div class="WwaFooter-inner">' +
      '<a class="WwaFooter-faq" href="/faq">Frequently Asked Questions</a>' +
      '<span class="WwaFooter-copy">&copy; 2001&ndash;' + year +
      ' Michael Ivan Skye. All rights reserved.</span>' +
      '</div>';

    // SPA-navigate to /faq instead of a full page reload.
    const faqLink = footer.querySelector('.WwaFooter-faq');
    if (faqLink) {
      faqLink.addEventListener('click', (e) => {
        e.preventDefault();
        m.route.set('/faq');
      });
    }

    document.body.appendChild(footer);
  }

  // Common case: user followed the wiki auth-gate redirect, logged in via the
  // modal (which reloads the page with ?return= intact), and is now back here
  // logged in. Bounce them to the wiki without making them navigate manually.
  maybeRedirectToReturnUrl();

  const scheduleBodyClassSync = () => {
    requestAnimationFrame(syncBodyClass);
  };

  syncBodyClass();
  Discussion.prototype.courseModuleProgressStatus = Model.attribute('courseModuleProgressStatus');
  Discussion.prototype.isCourseModuleLocked = Model.attribute('isCourseModuleLocked');
  Discussion.prototype.courseModuleAvailableAt = Model.attribute('courseModuleAvailableAt', Model.transformDate);
  Post.prototype.actions = Model.attribute('actions');

  extend(ComposerBody.prototype, 'oninit', function () {
    if (!(this instanceof ReplyComposer) && !(this instanceof EditPostComposer)) return;

    const initial = this instanceof EditPostComposer && typeof this.attrs.post?.actions === 'function'
      ? (this.attrs.post.actions() || '')
      : '';

    this.composer.fields.actions = Stream(initial);

    // Cache the show/hide decision at oninit to prevent the field from
    // flickering out during typing redraws. For ReplyComposer, schedule
    // a deferred re-check because flarum/mentions inserts the
    // [POSTMENTION id="..."] for "Reply to post" *after* oninit resolves.
    this._courseIntegrationShow = computeShouldShowActionsField(this);
    if (this instanceof ReplyComposer) {
      setTimeout(() => {
        const refined = computeShouldShowActionsField(this);
        if (refined !== this._courseIntegrationShow) {
          this._courseIntegrationShow = refined;
          m.redraw();
        }
      }, 0);
    }

    const discussion = getCourseIntegrationDiscussion(this);
    if (isCourseModuleDiscussion(discussion)) {
      const minHeight = 420;
      if (this.composer && (typeof this.composer.height !== 'number' || this.composer.height < minHeight)) {
        this.composer.height = minHeight;
      }

      if (this instanceof ReplyComposer && this.attrs) {
        this.attrs.placeholder = app.translator.trans('deepintegrations-custom-blog-ecourse.forum.course_integration.editor_placeholder').toString();
      }
    }
  });

  extend(ComposerBody.prototype, 'view', function (vdom) {
    if (!shouldShowActionsField(this)) return;
    injectActionsFieldIntoComposerVdom(vdom, this);

    // Show the main-integration counter on the same first-level course-
    // module reply gate as the actions field. EditPostComposer is
    // excluded: editing an already-accepted reply shouldn't re-gate.
    if (this instanceof ReplyComposer) {
      injectMainIntegrationCounterIntoComposerVdom(vdom, this);
    }
  });

  extend(ReplyComposer.prototype, 'data', function (data) {
    const actions = this.composer?.fields?.actions;
    if (typeof actions === 'function') {
      data.actions = actions();
    }
  });

  extend(EditPostComposer.prototype, 'data', function (data) {
    const actions = this.composer?.fields?.actions;
    if (typeof actions === 'function') {
      data.actions = actions();
    }
  });

  extend(CommentPost.prototype, 'bodyItems', function (items) {
    const post = this.attrs?.post;
    const value = typeof post?.actions === 'function' ? post.actions() : null;
    if (!value || typeof value !== 'string' || value.trim() === '') return;
    if (this.isEditing && this.isEditing()) return;

    items.add(
      'courseIntegrationActions',
      <div className="CourseIntegrationActions-rendered">
        <div className="CourseIntegrationActions-rendered-label">{app.translator.trans('deepintegrations-custom-blog-ecourse.forum.course_integration.rendered_label')}</div>
        <div className="CourseIntegrationActions-rendered-body">{value}</div>
      </div>,
      50
    );
  });

  if (app.session) {
    app.session.on('loggedIn', syncBodyClass);
    app.session.on('loggedIn', maybeRedirectToReturnUrl);
  };

  syncBodyClass();
  // Second chance for the return-URL redirect. app.session.user may not be
  // populated yet during initializer phase, so the call above can no-op even
  // when the user is logged in. Page.oninit fires for the very first mount —
  // by then the session is set, and it fires before IndexPage.oncreate (which
  // SPA-redirects logged-in users to /courses and strips the query string).
  extend(Page.prototype, 'oninit', maybeRedirectToReturnUrl);
  extend(Page.prototype, 'oncreate', scheduleBodyClassSync);
  extend(Page.prototype, 'onupdate', scheduleBodyClassSync);

  extend(TextEditor.prototype, 'oninit', function () {
    if (!this.deepIntegrationsMicState) {
      this.deepIntegrationsMicState = createMicState();
    }

    if (typeof this.deepIntegrationsAudioWrapBound === 'undefined') {
      this.deepIntegrationsAudioWrapBound = false;
    }
  });

  override(IndexPage.prototype, 'hero', function (original) {
    if (!isLoggedIn()) {
      return original();
    }

    return (
      <div className="DeepIntegrationsMemberBanner">
        <p className="DeepIntegrationsMemberBanner-text">
          Welcome Back, <span className="DeepIntegrationsMemberBanner-name">{app.session.user.displayName()}</span>
        </p>
      </div>
    );
  });

  override(IndexPage.prototype, 'view', function (original) {
    if (isLoggedIn()) {
      return original();
    }

    return (
      <div className="WwaGate">
        <div className="WwaGate-backdrop" aria-hidden="true">
          <div className="WwaGate-sun" />
          <div className="WwaGate-kanji">武士道</div>
        </div>
        <div className="WwaGate-overlay" aria-hidden="true" />

        <div className="WwaGate-content">
          <div className="container">
            <div className="WwaGate-inner">
              <div className="WwaGate-badge">Free Enrollment · Nine Awakenings</div>
              <h1 className="WwaGate-title">
                Wealth Warrior
                <span className="WwaGate-title-accent">Awakenings 101</span>
              </h1>
              <p className="WwaGate-tagline">
                A daily dojo for becoming what you already know you should be.
                Read. Integrate. Act. Free to begin.
              </p>

              <button
                className="Button Wwa-cta Wwa-cta--large WwaGate-cta"
                onclick={openSignUpModal}
              >
                Begin the Path
              </button>

              <div className="WwaGate-login">
                Already enrolled?{' '}
                <a className="WwaGate-login-link" onclick={openLogInModal}>Log in</a>
              </div>

              <div className="WwaGate-note">
                <span
                  className="WwaGate-term"
                  title={'bushidō (boo-shee-doh) — "the way of the warrior" - literal translation'}
                >
                  武士道
                </span>{' '}
                · WEALTH WARRIOR
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  });

  override(SignUpModal.prototype, 'submitData', function (original) {
    const data = original();
    const courseEnrollmentKey = this.attrs.courseEnrollmentKey;

    if (!courseEnrollmentKey) {
      return data;
    }

    return {
      ...data,
      courseEnrollmentKey,
    };
  });

  // Register the /courses route
  app.routes.courses = { path: '/courses', component: CoursesPage };

  // Register the /all-discussions route
  app.routes.allDiscussions = { path: '/all-discussions', component: AllDiscussionsPage };

  // Register the /quantum-surf landing page route
  app.routes.quantumSurf = { path: '/quantum-surf', component: QuantumSurfLandingPage };

  // Register the /course-uncle-pata landing page route
  app.routes.courseUnclePata = { path: '/course-uncle-pata', component: UnclePataLandingPage };

  // Register the /wwa-101 landing page route (Wealth Warrior Awakenings 101)
  app.routes.wwa101 = { path: '/wwa-101', component: WwaLandingPage };

  // Register the /faq page route
  app.routes.faq = { path: '/faq', component: WwaFaqPage };

  // Redirect logged-in users from homepage to /courses
  extend(IndexPage.prototype, 'oncreate', function() {
    const currentRoute = m.route.get();

    if (isLoggedIn() && currentRoute === '/') {
      m.route.set(app.route('courses'));
    }

    // Redirect /blog to /courses
    if (currentRoute === '/blog') {
      m.route.set(app.route('courses'));
    }
  });

  // Update "All Discussions" nav link to point to /all-discussions
  extend(IndexPage.prototype, 'navItems', function(items) {
    if (items.has('allDiscussions') && app.session.user?.isAdmin?.()) {
      const item = items.get('allDiscussions');
      items.replace('allDiscussions',
        <a href={app.route('allDiscussions')} className="hasIcon">
          {item.children}
        </a>
      );
    } else if (items.has('allDiscussions')) {
      items.remove('allDiscussions');
    }

    // Remove Tags nav item
    if (items.has('tags')) {
      items.remove('tags');
    }

    // Remove all individual tag items from sidebar
    Object.keys(items.items).forEach(key => {
      if (key.startsWith('tag')) {
        items.remove(key);
      }
    });

    // Add Courses nav link with pen icon
    items.add(
      'courses',
      <LinkButton icon="fas fa-pen" href={app.route('courses')}>
        Courses
      </LinkButton>,
      10 // Priority (lower number = higher in list)
    );
  });

  // Override BlogOverview header to change return link from /blog to /courses
  const BlogOverview = flarum.extensions['deepintegrations-custom-blog-ecourse']?.['components/pages/BlogOverview'];
  if (BlogOverview) {
    override(BlogOverview.prototype, 'header', function(original) {
      if (!m.route.param('slug')) {
        return <h2>{app.translator.trans('deepintegrations-custom-blog-ecourse.forum.recent_posts')}</h2>;
      }

      const tag = app.store.all('tags').filter((tag) => tag.slug() === m.route.param('slug'));

      return (
        <h2>
          {tag && tag[0] && tag[0].name()}
          <small>
            {' '}
            - <Link href={app.route('courses')}>{app.translator.trans('deepintegrations-custom-blog-ecourse.forum.return_to_overview')}</Link>
          </small>
        </h2>
      );
    });

    // Strip the "Welcome Back" hero from the course modules (blog) page —
    // it belongs only on the /courses dashboard.
    override(BlogOverview.prototype, 'view', function (original) {
      const vdom = original();

      if (Array.isArray(vdom)) {
        return vdom.filter((node) => {
          const cls = node && node.attrs && node.attrs.className;
          return !(typeof cls === 'string' && cls.indexOf('DeepIntegrationsMemberBanner') !== -1);
        });
      }

      return vdom;
    });
  }

  extend(DiscussionPage.prototype, 'view', function (vdom) {
    const discussion = this.discussion;

    if (!discussion) return;

    // If the module is locked for this actor, replace the entire
    // DiscussionPage body with a splash that shows the unlock time
    // (when time-gated) and a link back to the course overview.
    if (!app.session.user?.isAdmin?.() && discussion.isCourseModuleLocked?.() === true) {
      if (vdom && Array.isArray(vdom.children)) {
        vdom.children.length = 0;
        vdom.children.push(renderCourseModuleLockedSplash(discussion));
      }
      return;
    }

    // Insert title header before the post stream
    const container = vdom.children.find(child =>
      child && child.attrs && child.attrs.className &&
      child.attrs.className.includes('DiscussionPage-discussion')
    );

    if (container && container.children) {
      // Add title header as first child
      container.children.unshift(
        <div className="DiscussionPage-titleHeader">
          <h1 className="DiscussionPage-title">{discussion.title()}</h1>
        </div>
      );
    }
  });

  extend(TextEditor.prototype, 'controlItems', function (items) {
    const canUpload = app.forum.attribute('fof-upload.canUpload');
    console.log('[audio-recorder] controlItems', { canUpload: !!canUpload });

    if (!canUpload) return;

    const state = this.deepIntegrationsMicState || createMicState();
    this.deepIntegrationsMicState = state;

    const buttonProps = getMicButtonProps(state);

    items.add(
      'deepintegrations-mic',
      m(TextEditorButton, {
        className: 'Button Button--icon Button--link',
        icon: buttonProps.icon,
        title: buttonProps.title,
        disabled: state.status === MIC_STATUS.uploading,
        onclick: () => {
          if (state.status === MIC_STATUS.uploading) return;

          if (state.status === MIC_STATUS.recording) {
            stopMicRecording(this);
            return;
          }

          startMicRecording(this);
        },
      })
    );
  });

  extend(TextEditor.prototype, 'oncreate', function () {
    if (!this.uploader || !this.attrs?.composer?.fields?.content) return;

    if (this.deepIntegrationsAudioWrapBound) return;

    this.deepIntegrationsAudioWrapBound = true;

    this.uploader.on('success', ({ file, addBBcode }) => {
      if (!addBBcode) return;

      requestAnimationFrame(() => {
        wrapUploadedAudioBbcode(this, file);
      });
    });
  });

  extend(TextEditor.prototype, 'onremove', function () {
    const state = this.deepIntegrationsMicState;

    if (!state) return;

    if (state.recorder && state.recorder.state === 'recording') {
      state.recorder.stop();
    }

    stopMicStream(state);
    this.deepIntegrationsMicState = null;
  });
});
